import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdPerson,
  MdTrendingUp,
  MdTrendingDown,
  MdSpeed,
  MdGrade,
  MdAssessment,
  MdCalendarToday,
  MdWorkOff,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdDateRange,
  MdGroup,
  MdEmojiEvents,
  MdAccessTime,
  MdStar,
  MdStarBorder,
  MdEdit,
  MdDownload,
  MdRefresh,
  MdArrowBackIos,
  MdArrowForwardIos
} from 'react-icons/md';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ReferenceLine } from 'recharts';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';

const WorkerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [worker, setWorker] = useState(null);
  
  // Performance filters
  const [periodType, setPeriodType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [workerStatus, setWorkerStatus] = useState('all');
  const [performanceData, setPerformanceData] = useState(null);
  const [hasPerformanceData, setHasPerformanceData] = useState(false);
  
  // Chart and calendar states
  const [selectedProductType, setSelectedProductType] = useState('all');
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-01-10');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [attendanceStartDate, setAttendanceStartDate] = useState('2024-01-01');
  const [attendanceEndDate, setAttendanceEndDate] = useState('2024-01-31');
  const [attendanceDates, setAttendanceDates] = useState([]);

  // Sample chart data for weight analytics
  const weightChartData = [
    { 
      date: '2024-01-01', 
      displayDate: '01/01', 
      // Overall weights
      grossWeight: 12000, 
      netWeight: 11000, 
      taredWeight: 1000, 
      totalWeight: 11000,
      // Peeled Nuts weights
      peeledGrossWeight: 8500,
      peeledNetWeight: 7700,
      peeledTaredWeight: 800,
      peeledTotalWeight: 7700,
      // Polished Nuts weights
      polishedGrossWeight: 3500,
      polishedNetWeight: 3300,
      polishedTaredWeight: 200,
      polishedTotalWeight: 3300
    },
    { 
      date: '2024-01-02', 
      displayDate: '01/02', 
      grossWeight: 12500, 
      netWeight: 11500, 
      taredWeight: 1000, 
      totalWeight: 11500,
      peeledGrossWeight: 8900,
      peeledNetWeight: 8050,
      peeledTaredWeight: 850,
      peeledTotalWeight: 8050,
      polishedGrossWeight: 3600,
      polishedNetWeight: 3450,
      polishedTaredWeight: 150,
      polishedTotalWeight: 3450
    },
    { 
      date: '2024-01-03', 
      displayDate: '01/03', 
      grossWeight: 13000, 
      netWeight: 12000, 
      taredWeight: 1000, 
      totalWeight: 12000,
      peeledGrossWeight: 9300,
      peeledNetWeight: 8400,
      peeledTaredWeight: 900,
      peeledTotalWeight: 8400,
      polishedGrossWeight: 3700,
      polishedNetWeight: 3600,
      polishedTaredWeight: 100,
      polishedTotalWeight: 3600
    },
    { 
      date: '2024-01-04', 
      displayDate: '01/04', 
      grossWeight: 12800, 
      netWeight: 11800, 
      taredWeight: 1000, 
      totalWeight: 11800,
      peeledGrossWeight: 9100,
      peeledNetWeight: 8260,
      peeledTaredWeight: 840,
      peeledTotalWeight: 8260,
      polishedGrossWeight: 3700,
      polishedNetWeight: 3540,
      polishedTaredWeight: 160,
      polishedTotalWeight: 3540
    },
    { 
      date: '2024-01-05', 
      displayDate: '01/05', 
      grossWeight: 13200, 
      netWeight: 12200, 
      taredWeight: 1000, 
      totalWeight: 12200,
      peeledGrossWeight: 9400,
      peeledNetWeight: 8540,
      peeledTaredWeight: 860,
      peeledTotalWeight: 8540,
      polishedGrossWeight: 3800,
      polishedNetWeight: 3660,
      polishedTaredWeight: 140,
      polishedTotalWeight: 3660
    },
    { 
      date: '2024-01-06', 
      displayDate: '01/06', 
      grossWeight: 13500, 
      netWeight: 12450, 
      taredWeight: 1050, 
      totalWeight: 12450,
      peeledGrossWeight: 9600,
      peeledNetWeight: 8715,
      peeledTaredWeight: 885,
      peeledTotalWeight: 8715,
      polishedGrossWeight: 3900,
      polishedNetWeight: 3735,
      polishedTaredWeight: 165,
      polishedTotalWeight: 3735
    },
    { 
      date: '2024-01-07', 
      displayDate: '01/07', 
      grossWeight: 13300, 
      netWeight: 12300, 
      taredWeight: 1000, 
      totalWeight: 12300,
      peeledGrossWeight: 9500,
      peeledNetWeight: 8610,
      peeledTaredWeight: 890,
      peeledTotalWeight: 8610,
      polishedGrossWeight: 3800,
      polishedNetWeight: 3690,
      polishedTaredWeight: 110,
      polishedTotalWeight: 3690
    },
    { 
      date: '2024-01-08', 
      displayDate: '01/08', 
      grossWeight: 13700, 
      netWeight: 12600, 
      taredWeight: 1100, 
      totalWeight: 12600,
      peeledGrossWeight: 9800,
      peeledNetWeight: 8820,
      peeledTaredWeight: 980,
      peeledTotalWeight: 8820,
      polishedGrossWeight: 3900,
      polishedNetWeight: 3780,
      polishedTaredWeight: 120,
      polishedTotalWeight: 3780
    },
    { 
      date: '2024-01-09', 
      displayDate: '01/09', 
      grossWeight: 13400, 
      netWeight: 12350, 
      taredWeight: 1050, 
      totalWeight: 12350,
      peeledGrossWeight: 9600,
      peeledNetWeight: 8645,
      peeledTaredWeight: 955,
      peeledTotalWeight: 8645,
      polishedGrossWeight: 3800,
      polishedNetWeight: 3705,
      polishedTaredWeight: 95,
      polishedTotalWeight: 3705
    },
    { 
      date: '2024-01-10', 
      displayDate: '01/10', 
      grossWeight: 13500, 
      netWeight: 12450, 
      taredWeight: 1050, 
      totalWeight: 12450,
      peeledGrossWeight: 9600,
      peeledNetWeight: 8715,
      peeledTaredWeight: 885,
      peeledTotalWeight: 8715,
      polishedGrossWeight: 3900,
      polishedNetWeight: 3735,
      polishedTaredWeight: 165,
      polishedTotalWeight: 3735
    }
  ];

  // Filter chart data based on date range and product type
  const getFilteredChartData = () => {
    let filtered = weightChartData.filter(item => 
      item.date >= startDate && item.date <= endDate
    );

    if (selectedProductType === 'peeled') {
      return filtered.map(item => ({
        ...item,
        date: item.displayDate,
        // Use peeled nuts specific weights
        grossWeight: item.peeledGrossWeight,
        netWeight: item.peeledNetWeight,
        taredWeight: item.peeledTaredWeight,
        totalWeight: item.peeledTotalWeight
      }));
    } else if (selectedProductType === 'polished') {
      return filtered.map(item => ({
        ...item,
        date: item.displayDate,
        // Use polished nuts specific weights
        grossWeight: item.polishedGrossWeight,
        netWeight: item.polishedNetWeight,
        taredWeight: item.polishedTaredWeight,
        totalWeight: item.polishedTotalWeight
      }));
    } else {
      return filtered.map(item => ({
        ...item,
        date: item.displayDate
      }));
    }
  };

  // Get current filtered summary data
  const getCurrentSummaryData = () => {
    const filteredData = getFilteredChartData();
    if (filteredData.length === 0) return null;
    
    // Get the latest data point
    const latestData = filteredData[filteredData.length - 1];
    
    return {
      grossWeight: latestData.grossWeight,
      netWeight: latestData.netWeight,
      taredWeight: latestData.taredWeight,
      totalWeight: latestData.totalWeight,
      peeledNuts: selectedProductType === 'peeled' ? latestData.netWeight : 
                  selectedProductType === 'polished' ? 0 : latestData.peeledNetWeight,
      polishedNuts: selectedProductType === 'polished' ? latestData.netWeight : 
                    selectedProductType === 'peeled' ? 0 : latestData.polishedNetWeight
    };
  };

  // Generate calendar data for current month using attendance_dates (present days)
  const generateCalendarData = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const calendar = [];
    const currentDate = new Date(startDate);
    const presentDatesSet = new Set(attendanceDates || []);

    for (let i = 0; i < 42; i++) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const isCurrentMonth = currentDate.getMonth() === month;
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const isPresent = presentDatesSet.has(dateStr);

      calendar.push({
        date: new Date(currentDate),
        dateStr,
        isCurrentMonth,
        isToday,
        isPresent,
        isAbsent: !isPresent,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return calendar;
  };

  const calendarData = generateCalendarData();

  // Sample worker data - in real app, this would be fetched based on ID
  const sampleWorkers = {
    'MP001': {
      id: 'MP001',
      name: 'Akosua Mensah',
      experience: '5 years',
      currentEfficiency: 94.2,
      avgEfficiency: 91.8,
      totalBatches: 156,
      totalWeight: 12450.5,
      grossWeight: 13500.0,
      netWeight: 12450.5,
      taredWeight: 1049.5,
      qualityGrade: 'A+',
      lastActive: '2024-01-10',
      status: 'active',
      startDate: '2019-03-15',
      firstWorkDate: '2019-03-15',
      lastWorkDate: '2024-01-10',
      isCurrentWorker: true,
      dailyAverage: 4.2,
      targetAchieved: true,
      targetRange: 'Above Target',
      targetPercentage: 105.2,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 1,
      absent3Consecutive: false,
      turnoverRisk: 'low',
      lastAbsentDate: '2024-01-10',
      totalWorkDays: 1240,
      saturdayWork: 180,
      workerRank: 2, // Rank among all workers
      workerPercentage: 85.5, // Performance percentage compared to all workers
      group: 'Table 1',
      workRecords: [
        { date: '2024-01-10', batches: 12, grossWeight: 95.5, taredWeight: 8.5, netWeight: 87.0, efficiency: 94.2, quality: 'A+', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-09', batches: 11, grossWeight: 88.3, taredWeight: 7.2, netWeight: 81.1, efficiency: 92.1, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-08', batches: 13, grossWeight: 102.1, taredWeight: 9.1, netWeight: 93.0, efficiency: 96.8, quality: 'A+', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-07', batches: 0, grossWeight: 0, taredWeight: 0, netWeight: 0, efficiency: 0, quality: 'N/A', present: false, productType: 'N/A' },
        { date: '2024-01-06', batches: 10, grossWeight: 78.9, taredWeight: 6.8, netWeight: 72.1, efficiency: 89.5, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-05', batches: 12, grossWeight: 94.2, taredWeight: 8.2, netWeight: 86.0, efficiency: 93.7, quality: 'A+', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-04', batches: 11, grossWeight: 86.7, taredWeight: 7.5, netWeight: 79.2, efficiency: 91.2, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-03', batches: 13, grossWeight: 98.4, taredWeight: 8.8, netWeight: 89.6, efficiency: 95.1, quality: 'A+', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-02', batches: 12, grossWeight: 92.8, taredWeight: 8.1, netWeight: 84.7, efficiency: 94.6, quality: 'A+', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-01', batches: 0, grossWeight: 0, taredWeight: 0, netWeight: 0, efficiency: 0, quality: 'N/A', present: false, productType: 'N/A' }
      ]
    },
    'MP002': {
      id: 'MP002',
      name: 'Ama Serwaa',
      experience: '3 years',
      currentEfficiency: 87.5,
      avgEfficiency: 85.2,
      totalBatches: 98,
      totalWeight: 7890.3,
      grossWeight: 8500.0,
      netWeight: 7890.3,
      taredWeight: 609.7,
      qualityGrade: 'A',
      lastActive: '2024-01-10',
      status: 'active',
      startDate: '2021-06-10',
      firstWorkDate: '2021-06-10',
      lastWorkDate: '2024-01-10',
      isCurrentWorker: true,
      dailyAverage: 3.8,
      targetAchieved: true,
      targetRange: 'At Target',
      targetPercentage: 98.7,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 0,
      absent3Consecutive: false,
      turnoverRisk: 'low',
      lastAbsentDate: null,
      totalWorkDays: 890,
      saturdayWork: 125,
      workerRank: 5,
      workerPercentage: 78.2,
      group: 'Table 2',
      workRecords: [
        { date: '2024-01-10', batches: 10, grossWeight: 78.2, taredWeight: 6.8, netWeight: 71.4, efficiency: 87.5, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-09', batches: 9, grossWeight: 72.1, taredWeight: 6.2, netWeight: 65.9, efficiency: 85.3, quality: 'A-', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-08', batches: 11, grossWeight: 85.4, taredWeight: 7.4, netWeight: 78.0, efficiency: 89.1, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-07', batches: 10, grossWeight: 76.8, taredWeight: 6.6, netWeight: 70.2, efficiency: 86.7, quality: 'A', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-06', batches: 9, grossWeight: 70.5, taredWeight: 6.1, netWeight: 64.4, efficiency: 84.2, quality: 'A-', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-05', batches: 10, grossWeight: 79.1, taredWeight: 6.8, netWeight: 72.3, efficiency: 88.4, quality: 'A', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-04', batches: 8, grossWeight: 65.3, taredWeight: 5.7, netWeight: 59.6, efficiency: 82.1, quality: 'B+', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-03', batches: 11, grossWeight: 83.7, taredWeight: 7.2, netWeight: 76.5, efficiency: 87.9, quality: 'A', present: true, productType: 'Peeled Nuts' },
        { date: '2024-01-02', batches: 10, grossWeight: 77.4, taredWeight: 6.7, netWeight: 70.7, efficiency: 86.2, quality: 'A', present: true, productType: 'Polished Nuts' },
        { date: '2024-01-01', batches: 0, grossWeight: 0, taredWeight: 0, netWeight: 0, efficiency: 0, quality: 'N/A', present: false, productType: 'N/A' }
      ]
    }
  };

  // Fetch worker details from API
  const fetchWorkerDetails = async () => {
    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-workers/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok && result.data) {
        const workerData = result.data;
        
        // Map API data to component format
        setWorker({
          id: workerData.worker_id || workerData.id,
          cardNumber: workerData.card_number,
          name: `${workerData.first_name} ${workerData.last_name}`,
          firstName: workerData.first_name,
          lastName: workerData.last_name,
          email: workerData.email || '',
          phone: workerData.phone,
          address: workerData.address || '',
          dateOfBirth: workerData.date_of_birth,
          gender: workerData.gender || '',
          startDate: workerData.start_date,
          workType: workerData.work_type,
          shift: workerData.shift,
          tableNumber: workerData.table_number,
          experience: workerData.experience || '',
          skills: workerData.skills || '',
          notes: workerData.notes || '',
          dailyTarget: parseFloat(workerData.daily_target || 0),
          emergencyContactName: workerData.emergency_contact_name || '',
          emergencyContactPhone: workerData.emergency_contact_phone || '',
          emergencyContactRelationship: workerData.emergency_contact_relationship || '',
          // Additional fields for display
          group: workerData.table_number,
          isCurrentWorker: true,
          dailyAverage: 0,
          netWeight: 0,
          totalWorkDays: 0,
          saturdayWork: 0,
          workerRank: 0,
          absent3Consecutive: false,
          absentToday: false,
          absentThisWeek: 0,
          absentThisMonth: 0,
          firstWorkDate: workerData.start_date,
          lastWorkDate: new Date().toISOString().split('T')[0],
          workRecords: []
        });

        // Save attendance dates (present days) if provided
        if (Array.isArray(workerData.attendance_dates)) {
          setAttendanceDates(workerData.attendance_dates);
        } else {
          setAttendanceDates([]);
        }
      } else {
        showToast(result.message || 'Worker not found', 'error');
        setTimeout(() => navigate('/admin/manualpeeling/worker-list'), 2000);
      }
    } catch (error) {
      showToast('Error fetching worker details: ' + error.message, 'error');
      console.error('Error:', error);
      setTimeout(() => navigate('/admin/manualpeeling/worker-list'), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch performance statistics for this specific worker
  const fetchPerformanceStatistics = async () => {
    if (periodType !== 'daily' && periodType !== 'monthly') {
      return;
    }

    if (!worker) return;

    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      let url = `https://usibras-web-backend.onrender.com/api/manual-peeling/statistics?period_type=${periodType}&worker_status=${workerStatus}`;
      
      if (periodType === 'daily') {
        url += `&selected_date=${selectedDate}`;
      } else if (periodType === 'monthly') {
        url += `&selected_month=${selectedMonth}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok && result.data) {
        // Filter data to show only this specific worker
        const workerPerformance = result.data.worker_performance_details?.find(
          w => w.card_number === worker.cardNumber || w.worker_id === worker.id
        );
        
        if (workerPerformance) {
          setPerformanceData(workerPerformance);
          setHasPerformanceData(true);
        } else {
          setPerformanceData(null);
          setHasPerformanceData(false);
        }
      } else {
        setPerformanceData(null);
        setHasPerformanceData(false);
      }
    } catch (error) {
      console.error('Error fetching performance statistics:', error);
      setPerformanceData(null);
      setHasPerformanceData(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Load worker data on mount
  useEffect(() => {
    fetchWorkerDetails();
  }, [id]);

  // Fetch performance data when worker is loaded or filters change
  useEffect(() => {
    if (worker && (periodType === 'daily' || periodType === 'monthly')) {
      fetchPerformanceStatistics();
    }
  }, [worker, periodType, selectedDate, selectedMonth, workerStatus]);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchWorkerDetails();
    if (periodType === 'daily' || periodType === 'monthly') {
      fetchPerformanceStatistics();
    }
    showToast('Worker profile refreshed successfully', 'success');
  };

  // Handle export
  const handleExport = () => {
    showToast('Worker report exported successfully', 'success');
  };

  if (!worker) {
    return <UsibrasLoader />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header with Profile Card */}
      <div className="relative">
        {/* Background Header */}
        <div className="h-64 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-emerald-600/90"></div>
          
          {/* Navigation */}
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/admin/manualpeeling/performance')}
                className="flex items-center space-x-2 text-white hover:text-green-200 transition-colors bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg"
              >
                <MdArrowBack className="text-xl" />
                <span>Back to Performance</span>
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors flex items-center"
                >
                  <MdRefresh className="mr-2" />
                  Refresh
                </button>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors flex items-center"
                >
                  <MdDownload className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Card */}
        <div className="relative -mt-20 mx-6">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Profile Header */}
            <div className="bg-green-600 p-8 text-white">
              <div className="flex items-center space-x-6">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30">
                    <MdPerson className="text-4xl text-white" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center bg-green-500">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
                
                {/* Profile Info */}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold mb-2 text-white">{worker.name}</h1>
                  <p className="text-white text-lg mb-3">Card Number: {worker.cardNumber}</p>
                  <div className="flex items-center space-x-4">
                    <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                      {worker.workType || 'Full Time'}
                    </span>
                    {worker.phone && (
                      <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                        {worker.phone}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="text-right">
                  <div className="text-4xl font-bold mb-1 text-white">{worker.dailyTarget?.toFixed(1) || '0.0'} kg</div>
                  <div className="text-white text-sm">Daily Target</div>
                  <div className="text-2xl font-semibold mt-2 text-white">{worker.tableNumber || 'N/A'}</div>
                  <div className="text-white text-sm">Table Number</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 pb-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-green-400 to-green-600 rounded-xl">
                  <MdSpeed className="text-2xl text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">{worker.dailyTarget?.toFixed(1) || '0.0'} kg</div>
                  <div className="text-sm text-gray-500">Daily Target</div>
                </div>
              </div>
              <div className="flex items-center text-sm text-green-600">
                <MdTrendingUp className="mr-1" />
                <span>{worker.workType || 'Full Time'}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-green-400 to-green-600 rounded-xl">
                  <MdPerson className="text-2xl text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">{worker.shift || 'Day Shift'}</div>
                  <div className="text-sm text-gray-500">Shift</div>
                </div>
              </div>
              <div className="flex items-center text-sm text-green-600">
                <MdPerson className="mr-1" />
                <span>{worker.workType || 'Full Time'}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl">
                  <MdGroup className="text-2xl text-white" />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{worker.group || 'N/A'}</div>
                  <div className="text-sm text-gray-500">Table Name</div>
                </div>
              </div>
              <div className="flex items-center text-sm text-purple-600">
                <MdGroup className="mr-1" />
                <span>Work Group Assignment</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl">
                  <MdEmojiEvents className="text-2xl text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">{worker.experience || 'N/A'}</div>
                  <div className="text-sm text-gray-500">Experience</div>
                </div>
              </div>
              <div className="flex items-center text-sm text-purple-600">
                <MdEmojiEvents className="mr-1" />
                <span>Work Experience</span>
              </div>
            </div>
          </div>
        </div>

         {/* Detailed Information Grid */}
         {/* <div className="grid grid-cols-1 gap-6 mb-8"> */}
           {/* Weight Analytics Line Chart - COMMENTED OUT */}
           {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
             <div className="bg-gradient-to-r from-green-500 to-green-600 p-4">
               <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                 <h3 className="text-lg font-semibold text-white flex items-center">
                   <MdAssessment className="mr-2" />
                   Weight Analytics
                 </h3>
                 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                   <div className="flex items-center space-x-2">
                     <input
                       type="date"
                       className="px-3 py-1 text-sm rounded-lg bg-white/20 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                       value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                     />
                     <span className="text-white text-sm">to</span>
                     <input
                       type="date"
                       className="px-3 py-1 text-sm rounded-lg bg-white/20 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                       value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                     />
                   </div>
                   <select
                     className="px-3 py-1 text-sm rounded-lg bg-white/20 text-white border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                     value={selectedProductType}
                     onChange={(e) => setSelectedProductType(e.target.value)}
                   >
                     <option value="all" className="text-gray-800">All Products</option>
                     <option value="peeled" className="text-gray-800">Peeled Nuts Only</option>
                     <option value="polished" className="text-gray-800">Polished Nuts Only</option>
                   </select>
                 </div>
               </div>
             </div>
             <div className="p-6">
               <div className="h-80 bg-white rounded-xl p-6 mb-6 border border-gray-200">
                 <ResponsiveContainer width="100%" height="100%">
                   <LineChart data={getFilteredChartData()}>
                     <defs>
                       <linearGradient id="grossWeightGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                         <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                       </linearGradient>
                       <linearGradient id="netWeightGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                         <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                       </linearGradient>
                       <linearGradient id="taredWeightGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                         <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                       </linearGradient>
                       <linearGradient id="peeledNutsGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                         <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                       </linearGradient>
                       <linearGradient id="polishedNutsGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                         <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                       </linearGradient>
                     </defs>
                     <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                     <XAxis 
                       dataKey="date" 
                       axisLine={false}
                       tickLine={false}
                       tick={{ fill: '#6b7280', fontSize: 12 }}
                     />
                     <YAxis 
                       axisLine={false}
                       tickLine={false}
                       tick={{ fill: '#6b7280', fontSize: 12 }}
                       tickFormatter={(value) => `${(value/1000).toFixed(1)}k`}
                     />
                     <Tooltip 
                       contentStyle={{
                         backgroundColor: 'white',
                         border: '1px solid #e5e7eb',
                         borderRadius: '8px',
                         boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                       }}
                       formatter={(value, name) => {
                         const formattedValue = `${value.toFixed(1)} kg`;
                         const labelMap = {
                           grossWeight: selectedProductType === 'all' ? 'Gross Weight' : 
                                       selectedProductType === 'peeled' ? 'Peeled Gross Weight' : 'Polished Gross Weight',
                           netWeight: selectedProductType === 'all' ? 'Net Weight' : 
                                     selectedProductType === 'peeled' ? 'Peeled Net Weight' : 'Polished Net Weight',
                           taredWeight: selectedProductType === 'all' ? 'Tared Weight' : 
                                       selectedProductType === 'peeled' ? 'Peeled Tared Weight' : 'Polished Tared Weight',
                           totalWeight: selectedProductType === 'all' ? 'Total Weight' : 
                                       selectedProductType === 'peeled' ? 'Peeled Total Weight' : 'Polished Total Weight'
                         };
                         return [formattedValue, labelMap[name] || name];
                       }}
                       labelFormatter={(label) => `Date: ${label}`}
                     />
                     <Legend />
                     <Line 
                       type="monotone" 
                       dataKey="grossWeight" 
                       stroke="#ef4444" 
                       strokeWidth={3}
                       dot={{ fill: '#ef4444', stroke: '#ffffff', strokeWidth: 2, r: 4 }}
                       activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2, fill: '#ffffff' }}
                       name="Gross Weight"
                     />
                     <Line 
                       type="monotone" 
                       dataKey="netWeight" 
                       stroke="#10b981" 
                       strokeWidth={3}
                       dot={{ fill: '#10b981', stroke: '#ffffff', strokeWidth: 2, r: 4 }}
                       activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#ffffff' }}
                       name="Net Weight"
                     />
                     <Line 
                       type="monotone" 
                       dataKey="taredWeight" 
                       stroke="#f59e0b" 
                       strokeWidth={3}
                       dot={{ fill: '#f59e0b', stroke: '#ffffff', strokeWidth: 2, r: 4 }}
                       activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2, fill: '#ffffff' }}
                       name="Tared Weight"
                     />
                     <ReferenceLine 
                       y={5000} 
                       stroke="#6b7280" 
                       strokeWidth={2} 
                       strokeDasharray="5 5"
                       label={{ value: "Target (5,000 kg)", position: "topRight", style: { fill: '#6b7280', fontSize: '12px', fontWeight: 'bold' } }}
                     />
                   </LineChart>
                 </ResponsiveContainer>
               </div>

               <div className="grid grid-cols-3 gap-4">
                 {(() => {
                   const summaryData = getCurrentSummaryData();
                   if (!summaryData) return null;
                   
                   return (
                     <>
                       <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-sm font-medium text-red-600">
                               {selectedProductType === 'peeled' ? 'Peeled Gross Weight' :
                                selectedProductType === 'polished' ? 'Polished Gross Weight' : 'Gross Weight'}
                             </p>
                             <p className="text-2xl font-bold text-red-800">{summaryData.grossWeight.toFixed(1)} kg</p>
                           </div>
                           <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                             <MdAssessment className="text-white text-xl" />
                           </div>
                         </div>
                       </div>
                       <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-sm font-medium text-green-600">
                               {selectedProductType === 'peeled' ? 'Peeled Net Weight' :
                                selectedProductType === 'polished' ? 'Polished Net Weight' : 'Net Weight'}
                             </p>
                             <p className="text-2xl font-bold text-green-800">{summaryData.netWeight.toFixed(1)} kg</p>
                           </div>
                           <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                             <MdCheckCircle className="text-white text-xl" />
                           </div>
                         </div>
                       </div>
                       <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-4 rounded-xl">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-sm font-medium text-amber-600">
                               {selectedProductType === 'peeled' ? 'Peeled Tared Weight' :
                                selectedProductType === 'polished' ? 'Polished Tared Weight' : 'Tared Weight'}
                             </p>
                             <p className="text-2xl font-bold text-amber-800">{summaryData.taredWeight.toFixed(1)} kg</p>
                           </div>
                           <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                             <MdGrade className="text-white text-xl" />
                           </div>
                         </div>
                       </div>
                     </>
                   );
                 })()}
               </div>

               <div className="mt-6 bg-gray-50 rounded-xl p-6">
                 <h4 className="text-lg font-semibold text-gray-800 mb-4">Product Types Over Time</h4>
                 <div className="grid grid-cols-2 gap-4">
                   {(() => {
                     const summaryData = getCurrentSummaryData();
                     if (!summaryData) return null;
                     
                     return (
                       <>
                         <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                           <div className="flex items-center">
                             <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                             <span className="font-medium text-gray-700">Peeled Nuts</span>
                           </div>
                           <span className="font-bold text-green-600">
                             {selectedProductType === 'peeled' ? summaryData.netWeight.toFixed(1) :
                              selectedProductType === 'polished' ? '0.0' : summaryData.peeledNuts.toFixed(1)} kg
                           </span>
                         </div>
                         <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                           <div className="flex items-center">
                             <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                             <span className="font-medium text-gray-700">Polished Nuts</span>
                           </div>
                           <span className="font-bold text-blue-600">
                             {selectedProductType === 'polished' ? summaryData.netWeight.toFixed(1) :
                              selectedProductType === 'peeled' ? '0.0' : summaryData.polishedNuts.toFixed(1)} kg
                           </span>
                         </div>
                       </>
                     );
                   })()}
                 </div>
               </div>
             </div>
           </div> */}

        {/* </div> */}

        {/* Performance Metrics Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdCalendarToday className="mr-2 text-blue-500" />
            Performance Period Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Period Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">View Period</label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                <option value="daily">Daily View</option>
                <option value="monthly">Monthly View</option>
              </select>
            </div>

            {/* Date Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {periodType === 'daily' ? 'Select Date' : 'Select Month'}
              </label>
              {periodType === 'daily' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                />
              )}
              {periodType === 'monthly' && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                />
              )}
            </div>

            {/* Worker Status - Static */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Worker Status</label>
              <div className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm font-medium text-gray-700 cursor-not-allowed">
                All
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics Summary */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8 relative">
          
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <MdTrendingUp className="mr-2" />
              Performance Metrics - {periodType === 'daily' ? 'Daily' : periodType === 'monthly' ? 'Monthly' : 'Yearly'}
            </h2>
          </div>
          <div className="p-6">
            {/* No Data Message */}
            {(periodType === 'daily' || periodType === 'monthly') && !hasPerformanceData && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                <div className="flex items-center">
                  <MdWarning className="text-3xl text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">No Performance Data</p>
                    <p className="text-xs text-yellow-700">
                      No performance data found for this worker in the selected {periodType === 'daily' ? 'date' : 'month'}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Card Number */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                <p className="text-xs text-blue-600 font-medium mb-1">Card Number</p>
                <p className="text-2xl font-bold text-blue-800">
                  {performanceData?.card_number || worker.cardNumber}
                </p>
              </div>

              {/* Work Status */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
                <p className="text-xs text-green-600 font-medium mb-1">Work Status</p>
                <p className="text-lg font-bold text-green-800">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    (performanceData?.work_status || 'Current') === 'Current' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {performanceData?.work_status || 'Current'}
                  </span>
                </p>
              </div>

              {/* Group Name */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl">
                <p className="text-xs text-purple-600 font-medium mb-1">Group Name</p>
                <p className="text-2xl font-bold text-purple-800">
                  {performanceData?.group_name || worker.tableNumber}
                </p>
              </div>

              {/* Net Weight */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
                <p className="text-xs text-green-600 font-medium mb-1">Net Weight</p>
                <p className="text-2xl font-bold text-green-800">
                  {performanceData?.net_weight ? `${performanceData.net_weight.toFixed(1)} kg` : '-- kg'}
                </p>
              </div>

              {/* Target */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                <p className="text-xs text-blue-600 font-medium mb-1">Target</p>
                <p className="text-2xl font-bold text-blue-800">
                  {performanceData?.target ? `${performanceData.target.toFixed(1)} kg` : `${worker.dailyTarget?.toFixed(1)} kg`}
                </p>
              </div>

              {/* Deviation from Target */}
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-4 rounded-xl">
                <p className="text-xs text-amber-600 font-medium mb-1">Deviation from Target</p>
                <p className="text-xl font-bold text-amber-800">
                  {performanceData?.deviation_from_target !== undefined ? (
                    <span className={performanceData.deviation_from_target >= 0 ? 'text-green-700' : 'text-red-700'}>
                      {performanceData.deviation_from_target >= 0 ? '+' : ''}{performanceData.deviation_from_target.toFixed(1)} kg
                    </span>
                  ) : '-- kg'}
                </p>
              </div>

              {/* Group Average */}
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-4 rounded-xl">
                <p className="text-xs text-indigo-600 font-medium mb-1">Group Average</p>
                <p className="text-2xl font-bold text-indigo-800">
                  {performanceData?.group_average ? `${performanceData.group_average.toFixed(1)} kg` : '-- kg'}
                </p>
              </div>

              {/* Deviation from Group Average */}
              <div className="bg-gradient-to-r from-pink-50 to-pink-100 p-4 rounded-xl">
                <p className="text-xs text-pink-600 font-medium mb-1">Deviation from Group Avg</p>
                <p className="text-xl font-bold text-pink-800">
                  {performanceData?.deviation_from_group_average !== undefined ? (
                    <span className={performanceData.deviation_from_group_average >= 0 ? 'text-green-700' : 'text-red-700'}>
                      {performanceData.deviation_from_group_average >= 0 ? '+' : ''}{performanceData.deviation_from_group_average.toFixed(1)} kg
                    </span>
                  ) : '-- kg'}
                </p>
              </div>

              {/* Expected Working Days - Only for monthly */}
              {periodType === 'monthly' && (
                <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 p-4 rounded-xl">
                  <p className="text-xs text-cyan-600 font-medium mb-1">Expected Working Days</p>
                  <p className="text-2xl font-bold text-cyan-800">
                    {performanceData?.expected_working_days || '--'}
                  </p>
                </div>
              )}

              {/* Actual Working Days - Only for monthly */}
              {periodType === 'monthly' && (
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-4 rounded-xl">
                  <p className="text-xs text-teal-600 font-medium mb-1">Actual Working Days</p>
                  <p className="text-2xl font-bold text-teal-800">
                    {performanceData?.actual_working_days || '--'}
                  </p>
                </div>
              )}

              {/* Weekend Working Days - Only for monthly */}
              {periodType === 'monthly' && (
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl">
                  <p className="text-xs text-orange-600 font-medium mb-1">Weekend Working Days</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {performanceData?.weekend_days_worked || '--'}
                  </p>
                </div>
              )}

              {/* Bonus - Only for monthly */}
              {periodType === 'monthly' && (
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 p-4 rounded-xl">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Bonus</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    {performanceData?.bonus_amount !== undefined ? `GH₵ ${performanceData.bonus_amount.toFixed(2)}` : 'GH₵ --'}
                  </p>
                </div>
              )}

              {/* Absent 3 Consecutive */}
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl">
                <p className="text-xs text-red-600 font-medium mb-1">Absent 3 Consecutive</p>
                <p className="text-lg font-bold text-red-800">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    (performanceData?.absent_3_consecutive || 'No') === 'Yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {performanceData?.absent_3_consecutive || 'No'}
                  </span>
                </p>
              </div>
            </div>

            {/* Current Filter Display */}
            {(periodType === 'daily' || periodType === 'monthly') && (
              <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <MdCalendarToday className="text-blue-600 mr-2 text-xl" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Currently Viewing: </span>
                    <span className="text-base font-bold text-blue-800">
                      {periodType === 'daily' && new Date(selectedDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                      {periodType === 'monthly' && new Date(selectedMonth + '-01').toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long' 
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Attendance Calendar */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          {/* Attendance Calendar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden relative">
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* <h3 className="text-lg font-semibold text-white flex items-center">
                  <MdCalendarToday className="mr-2" />
                  Attendance
                </h3> */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      className="px-3 py-1 text-sm rounded-lg bg-white/20 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                      value={attendanceStartDate}
                      onChange={(e) => setAttendanceStartDate(e.target.value)}
                    />
                    <span className="text-white text-sm">to</span>
                    <input
                      type="date"
                      className="px-3 py-1 text-sm rounded-lg bg-white/20 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                      value={attendanceEndDate}
                      onChange={(e) => setAttendanceEndDate(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-1 text-white hover:bg-white/20 rounded"
                    >
                      <MdArrowBackIos className="text-sm" />
                    </button>
                    <span className="text-white font-medium min-w-[120px] text-center">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-1 text-white hover:bg-white/20 rounded"
                    >
                      <MdArrowForwardIos className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600 bg-gray-50 rounded">
                    {day}
                  </div>
                ))}
                {calendarData.map((day, index) => (
                  <div
                    key={index}
                    className={`p-2 text-center text-sm rounded cursor-pointer transition-all duration-200 relative ${
                      !day.isCurrentMonth
                        ? 'text-gray-300 bg-gray-50'
                        : day.isToday
                        ? 'bg-blue-500 text-white font-bold ring-2 ring-blue-300'
                        : day.isPresent
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                    title={`${day.date.toLocaleDateString()} - ${day.isPresent ? 'Present' : 'Absent'}`}
                  >
                    {day.date.getDate()}
                  </div>
                ))}
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-100 rounded"></div>
                  <span className="text-gray-600">Present</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-100 rounded"></div>
                  <span className="text-gray-600">Absent</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-gray-600">Today</span>
                </div>
              </div>
            </div>
          </div>

          
        </div>

        {/* Additional Worker Information */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <MdAssessment className="mr-2" />
              Worker Details
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">Personal Information</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Full Name</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Email</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Phone</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Address</span>
                    <span className="text-sm font-semibold text-gray-900 text-right max-w-xs">{worker.address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">Work Information</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Table Number</span>
                    <span className="text-sm font-semibold text-purple-600">{worker.tableNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Work Type</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.workType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Shift</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.shift}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Start Date</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {worker.startDate ? new Date(worker.startDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">Emergency Contact</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Contact Name</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.emergencyContactName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Contact Phone</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.emergencyContactPhone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Relationship</span>
                    <span className="text-sm font-semibold text-gray-900">{worker.emergencyContactRelationship || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">Additional Notes</h4>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600 block mb-1">Skills</span>
                    <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                      {worker.skills || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 block mb-1">Notes</span>
                    <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                      {worker.notes || 'No notes available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default WorkerProfile;
